import { z as e } from "zod";
const u = e.object({
  type: e.literal("text"),
  text: e.string().min(1)
}).meta({
  id: "TextPart",
  title: "TextPart",
  description: "Free-text markdown. Used as the top-level fallback when no story qualifies."
}), d = e.enum(["positive", "negative", "neutral"]).meta({
  id: "Sentiment",
  title: "Sentiment",
  description: "Directional tone for values. Apps maps each value to theme tokens at render time."
}), n = e.object({
  text: e.string().min(1),
  sentiment: d.nullish().describe("Absent renders neutral.")
}).meta({
  id: "SentimentText",
  title: "SentimentText",
  description: "Text fragment with an optional directional tone. The producer delivers preformatted, localized text; the renderer maps sentiment to theme tokens."
}), b = e.enum(["active", "silent"]).meta({
  id: "PulseStatus",
  title: "PulseStatus",
  description: "Status of one week on the order-pulse calendar. active means at least one order, silent means none. Weeks arrive in chronological order ending with the current week, so the renderer derives the ISO label and the current-week highlight from array position and today."
}), y = e.object({
  minDays: e.number().int().min(1).max(365),
  maxDays: e.number().int().min(1).max(365)
}).meta({
  id: "NormalCycle",
  title: "NormalCycle",
  description: "Customer's normal order cycle as a range of days. minDays equals maxDays for a single-value cycle. The renderer composes the localized wording (e.g. 'alle 7–10 Tage' or 'alle 7 Tage')."
}), h = e.object({
  id: e.uuid(),
  name: e.string().min(1),
  isGroup: e.boolean().nullish().describe(
    "Whether the id names a product group rather than an individual product. Absent when the producer cannot tell which of the two grains the id was drawn from."
  )
}).meta({
  id: "Product",
  title: "Product",
  description: "Product reference. id is Acto's internal UUID for the product, used for drill-down. name is the display label."
}), R = e.enum(["growth", "decline"]).meta({
  id: "ProductTrend",
  title: "ProductTrend",
  description: "Direction a product row moved in. A productKpis drill orders the children the same way: largest growth or largest decline first."
}), f = e.enum(["revenue", "profit", "margin", "orders"]).meta({
  id: "OrderKpis",
  title: "OrderKpis",
  description: "The KPI a figure is measured in. revenue and profit are currency amounts, margin is a ratio, orders is a count, so the renderer picks the unit from this value."
}), o = e.object({
  start: e.iso.date(),
  end: e.iso.date()
}).meta({
  id: "DateRangeWindow",
  title: "DateRangeWindow",
  description: "Inclusive ISO date range. start and end follow YYYY-MM-DD. The renderer formats per locale at display time."
}), x = e.object({
  current: o,
  comparison: o
}).meta({
  id: "DateRange",
  title: "DateRange",
  description: "Current and comparison ISO date ranges. The renderer formats them per locale (e.g. 'Apr 2025 – Mär 2026' in de-DE)."
}), D = e.object({
  kpi: f,
  dateRange: o
}).meta({
  id: "KpiPeriod",
  title: "KpiPeriod",
  description: "What a card's numeric figures measure and the period they cover."
}), A = () => e.string().min(1).nullish().describe(
  "Producer-owned identity of the card, e.g. the analytics signal it renders ('declining_groups'). Together with the prep's sessionId it identifies the card for per-card feedback; the renderer treats it as opaque. Absent means the card cannot be hidden."
), j = (t) => ({
  type: e.literal(t),
  cardKey: A()
}), r = (t, i) => e.object({ ...j(t), ...i }), M = e.enum(["currency", "number", "percent", "duration"]).meta({
  id: "ValueFormat",
  title: "ValueFormat",
  description: "Renderer formatting hint for numeric values. The agent picks the right format per metric. The renderer applies locale and currency from chart meta."
}), K = e.enum(["comparison", "projected"]).meta({
  id: "YSeriesStyle",
  title: "YSeriesStyle",
  description: "Relative role of a y-series. The lead series carries no style. comparison flags a reference series (prior period, peer average). projected flags forward-looking extrapolation."
}), I = e.object({
  field: e.string().min(1).max(60),
  label: e.string().min(1).nullish(),
  axisType: e.enum(["category", "linear", "time"])
}).meta({
  id: "XAxis",
  title: "XAxis",
  description: "X-axis spec. field references a key in chart data. label is the optional rendered axis title. axisType picks the scale: category for nominal, linear for numeric, time for date-like."
}), L = e.object({
  field: e.string().min(1).max(60),
  label: e.string().min(1),
  valueFormat: M.nullish(),
  style: K.nullish(),
  sentiment: d.nullish().describe("Absent renders neutral.")
}).meta({
  id: "YSeries",
  title: "YSeries",
  description: "One y-series on a cartesian chart. field reads the value from each data row. label is the legend entry. valueFormat hints how to render the numbers. style flags relative roles."
}), E = e.object({
  currency: e.string().length(3).nullish(),
  locale: e.string().min(2).max(10).nullish()
}).meta({
  id: "ChartMeta",
  title: "ChartMeta",
  description: "Chart-level metadata. currency follows ISO 4217 (EUR, USD). locale follows BCP 47 (de-DE, en-US). Both feed renderer formatting."
}), U = e.union([e.string(), e.number(), e.null()]), z = e.record(e.string(), U), p = e.object({
  chartType: e.literal("bar"),
  x: I,
  y: e.array(L).min(1).max(4),
  data: e.array(z).min(1).max(60).describe(
    "Chart data rows. Each row is a key-value bag whose keys match the field strings declared on x and y. Values are numeric for measures, string for category labels, null for explicit gaps."
  ),
  meta: E.nullish()
}).meta({
  id: "BarChart",
  title: "BarChart",
  description: "Cartesian bar chart. One x axis, one to four y series. Grouped bars when y has multiple series, single bars when one. Used inline by cards (e.g. Trend) and standalone in chat."
}), Y = r("trend", {
  headline: e.string().min(1).describe(
    "Names the metric, e.g. 'Umsatzentwicklung', never the percentage."
  ),
  dateRange: x,
  chart: p,
  trend: e.number().nullish().describe(
    "Precomputed trend delta as a percentage (e.g. -34 or 18). When present, the renderer uses this value directly instead of deriving it from chart.data. Absent falls back to the renderer's own calculation."
  )
}).meta({
  id: "Trend",
  title: "Trend",
  description: "Trend card. A grouped bar chart comparing the current period against a reference. Visual tone follows the parent story archetype, the renderer derives it. Position in the story (hero vs body) is positional, not a card property."
}), F = r("orderPulseHero", {
  weeks: e.array(b).length(24).describe(
    "24 weekly statuses in chronological order ending with the current week. Derive this and lastOrder from the same orders query in one pass: the week bucket containing lastOrder must be 'active', and the count of trailing 'silent' buckets must be consistent with days since lastOrder (each silent bucket represents one week without an order)."
  ),
  normalCycle: y,
  lastOrder: e.iso.date().describe(
    "ISO date of the last order (YYYY-MM-DD). The renderer derives the silence headline ('Stille seit N Tagen') by computing days since lastOrder. Must agree with weeks[]: the week bucket containing this date must be 'active'. Derive both fields from the same orders query."
  )
}).meta({
  id: "OrderPulseHero",
  title: "OrderPulseHero",
  description: "Hero pulse calendar. 24 weeks of order rhythm with the silence pattern as the story. The renderer derives the silence headline from lastOrder and draws the active/silent segments from weeks."
}), B = r("orderPulseCalendar", {
  weeks: e.array(b).length(12).describe(
    "12 weekly statuses in chronological order ending with the current week. Derive this and lastOrder from the same orders query in one pass: the week bucket containing lastOrder must be 'active', and the count of trailing 'silent' buckets must be consistent with days since lastOrder (each silent bucket represents one week without an order)."
  ),
  normalCycle: y,
  lastOrder: e.iso.date().describe(
    "ISO date of the last order (YYYY-MM-DD). The renderer derives the cycle-drift headline (e.g. 'Zyklus: 7–10 d → 23 d' / 'Cycle: 7–10 d → 23 d') by combining normalCycle with days-since-lastOrder. Must agree with weeks[]: the week bucket containing this date must be 'active'. Derive both fields from the same orders query."
  )
}).meta({
  id: "OrderPulseCalendar",
  title: "OrderPulseCalendar",
  description: "Body pulse calendar. 12 weeks of order rhythm with a cycle-drift framing. The renderer derives the cycle-delta headline from normalCycle and the days since lastOrder; the current-week marker comes from the last array position."
}), G = e.object({
  product: h.meta({
    deprecated: !0,
    description: "Superseded by products."
  }),
  products: e.array(h).nullish().describe(
    "Every product the row covers. The deltas cover the whole set, not one member of it."
  ),
  absoluteDelta: e.number().describe(
    "Absolute change (positive or negative), in the unit of kpiPeriod.kpi when that is set and in the card's currency otherwise."
  ),
  percentDelta: e.number().describe("Percentage change as a signed number (-22 for '-22%').")
}).meta({
  id: "GapListItem",
  title: "GapListItem",
  description: "One row in a gap list: the products it covers and their change over the card's period."
}), q = r("gapList", {
  headline: e.string().min(1).describe(
    "One-sentence editorial reasoning for the row set, e.g. '90% des Umsatzverlusts entfallen auf diese Produktgruppen'. Says why these products matter, without repeating the per-row values."
  ),
  currency: e.string().length(3).describe(
    "ISO 4217 currency code (e.g. 'EUR'). Applies to every item's absoluteDelta."
  ),
  kpiPeriod: D.nullish().describe(
    "What every item delta measures and the period it covers. Absent when either is unknown."
  ),
  items: e.array(G).min(1).max(6)
}).meta({
  id: "GapList",
  title: "GapList",
  description: "Ranked products with deltas. Item order is significant. The sign of the deltas carries the direction, so the card has no separate field for it."
}), N = e.object({
  question: e.string().min(1),
  rationale: e.string().min(1).nullish()
}).meta({
  id: "QuestionPromptItem",
  title: "QuestionPromptItem",
  description: "One conversation prompt. rationale ties the question to the active story."
}), V = r("questionPrompts", {
  items: e.array(N).min(1)
}).meta({
  id: "QuestionPrompts",
  title: "QuestionPrompts",
  description: "Conversation prompts for the rep. Each item is a question paired with a rationale. Framing follows the parent story archetype: the renderer derives any growth-vs-default styling from there. Composition is the agent's call."
}), w = () => ({
  validUntil: e.iso.date().nullish().describe(
    "ISO date the offer expires (YYYY-MM-DD). The renderer formats per locale, e.g. '27. August 2026'. Absent when the offer has no expiry."
  ),
  externalReference: e.string().min(1).describe(
    "Source-system offer ID (Angebotsnummer). Shown on the card and used for drill-down."
  )
}), W = r("offer", {
  ...w(),
  kind: e.literal("value"),
  value: e.number().min(0).describe(
    "Offer value (Angebotswert) in the card's currency. Use this variant when the source offer carries a concrete monetary amount."
  ),
  currency: e.string().length(3).describe("ISO 4217 currency code (e.g. 'EUR'). Applies to value.")
}).meta({
  id: "ValueOffer",
  title: "ValueOffer",
  description: "Monetary offer. The renderer shows a fixed 'Angebotswert' label above the formatted value, e.g. '10.463,40€'."
}), Q = r("offer", {
  ...w(),
  kind: e.literal("terms"),
  category: e.string().min(1).describe(
    "Offer category shown above the headline, e.g. 'Rahmenvertrag', 'Aktion', 'Konditionsvereinbarung'. Use the German term that matches the tenant's offer type."
  ),
  headline: e.string().min(1).describe(
    "One-line editorial summary of the offer terms, e.g. 'Rabatt 8% auf Toner'. No trailing punctuation, no quotes."
  )
}).meta({
  id: "TermsOffer",
  title: "TermsOffer",
  description: "Term-based offer (Rahmenvertrag, Konditionsvereinbarung, kategorischer Rabatt). Use this variant when the offer's substance is the arrangement and no single monetary amount represents it faithfully."
}), H = e.discriminatedUnion("kind", [W, Q]).meta({
  title: "Offer",
  description: "Concrete commercial hook. Pick 'value' when the source data carries a real Angebotswert. Pick 'terms' for framework agreements and conditional discounts where no single amount represents the offer."
}), J = r("kpiDelta", {
  metricLabel: e.string().min(1),
  value: e.string().min(1),
  baseline: e.object({
    label: e.string().min(1),
    value: e.string().min(1)
  }).describe(
    "Left footer slot. label is the short German caption (e.g. 'Ø Intervall', 'Bestellzeitpunkt'). value carries the comparison anchor or supporting context (e.g. '21 Tage', 'vor 96 Tagen')."
  ),
  delta: e.object({
    label: e.string().min(1),
    value: e.string().min(1)
  }).nullish().describe(
    "Optional right footer slot. Omit when one supporting fact is enough. label is the short German caption (e.g. 'Über Normalwert', 'Datum', 'Einordnung'). value carries the deviation, takeaway, or commentary (e.g. '+120%', '13 Feb 2026', 'Top-10 Order in der Kundenhistorie')."
  ),
  sentiment: d.nullish()
}).meta({
  id: "KpiDelta",
  title: "KpiDelta",
  description: "Single-metric body card. metricLabel plus value at the top, one or two labelled supporting slots at the bottom. baseline is always present; delta is optional when one fact is enough. Both footer slots own their own caption, so the card flexes between strict comparison framing (Ø Intervall vs +120%) and looser fact framing (Bestellzeitpunkt + Einordnung)."
}), X = e.object({
  label: e.string().min(1).describe(
    "Short caption above the value, e.g. 'Intervall', 'Gesamtumsatz (YTD)', 'Letzter Besuch'."
  ),
  value: n.describe(
    "Preformatted and localized by the producer, e.g. 'Ø 21 Tage', '31.993€', '02.03.2026'."
  ),
  subValue: n.nullish().describe(
    "Inline secondary value rendered after the value, e.g. '+582%' next to '31.993€' or '652€' next to '05.06.2026'. Preformatted and localized by the producer."
  )
}).meta({
  id: "OverviewItem",
  title: "OverviewItem",
  description: "One label/value pairing in the overview grid, with an optional inline secondary value. Each value fragment carries its own optional sentiment."
}), $ = r("overview", {
  label: e.string().min(1).nullish().describe(
    "Small caption above the headline, e.g. a metric name ('Letzte Bestellung') or an external customer number."
  ),
  headline: e.string().min(1).describe(
    "Prominent header text, e.g. the metric value ('703,12€') or the customer name."
  ),
  items: e.array(X).max(12)
}).meta({
  id: "Overview",
  title: "Overview",
  description: "Generic overview card: header block (label plus headline), then a divider, then 0..N label/value pairings. The card flexes between single-metric framing (label is the metric name, headline its value, items the supporting facts) and entity framing (label is an external reference, headline the entity name, items its KPIs). The renderer owns layout and responsive arrangement of the items."
}), v = e.object({
  chip: n.nullish().describe(
    "Leading badge: a short classifier or signal such as '-22 %', 'Neu' or 'Wiederansprache'. Preformatted and localized by the producer. Never repeats the figure shown in value."
  ),
  title: e.string().min(1).describe("Main value of the row, e.g. a product or client name."),
  subtitle: e.string().min(1).nullish().describe(
    "Secondary line under the title. Preformatted and localized by the producer, e.g. '-3.200 €' or 'Quelle: SAP'."
  ),
  value: n.nullish().describe(
    "Right-aligned value. Preformatted and localized by the producer, e.g. '-22 %' or '+850 €/Mo'."
  )
}).meta({
  id: "RowText",
  title: "RowText",
  description: "All strings arrive preformatted and localized from the producer."
}), _ = e.object({
  kind: e.literal("peerPotentials")
}).meta({
  id: "PeerPotentialsDrill",
  title: "PeerPotentialsDrill",
  description: "Opening the row shows what similar clients buy one level below the entity's product nodes, ranked by adoption."
}), Z = e.object({
  kind: e.literal("productKpis"),
  kpi: f,
  dateRange: o,
  trend: R
}).meta({
  id: "ProductKpisDrill",
  title: "ProductKpisDrill",
  description: "Opening the row shows the children of the entity's product groups with their kpi change over dateRange, ordered by trend."
}), ee = e.discriminatedUnion("kind", [_, Z]).meta({
  id: "ProductRowDrill",
  title: "ProductRowDrill",
  description: "Opens the entity of the row it belongs to and carries every other value it needs. The union grows additively."
}), te = e.object({
  ids: e.array(e.uuid()).min(1).describe(
    "Product hierarchy nodes the row stands for, as Acto's internal UUIDs. The first id is the representative node. Several ids mean the row aggregates them, and a drill opens their union."
  ),
  level: e.int().describe("Hierarchy level every id sits at."),
  drill: ee.nullish().describe(
    "Absent means the row is static."
  )
}).meta({
  id: "ProductEntity",
  title: "ProductEntity"
}), O = (t, i, a) => e.object({
  entityKind: e.literal(t),
  items: e.array(a).min(1).max(8)
}).meta({ id: i, title: i }), re = (t, i, a) => O(
  t,
  `${i}s`,
  v.and(e.object({ entity: a })).meta({
    id: i,
    title: i
  })
), ie = re(
  "product",
  "ProductRow",
  te.nullish().describe(
    "Absent means the producer could not resolve the product; the row keeps its place in the list."
  )
), ne = O("none", "TextRows", v), ae = (t) => e.discriminatedUnion("entityKind", t), se = ae([ie, ne]).meta({
  id: "EntityRows",
  title: "EntityRows",
  description: "One list never mixes entity kinds. The union grows one member per kind."
}), oe = r("entityList", {
  label: e.string().nullish().describe("Short meta label above the headline, e.g. 'Produkte'."),
  headline: e.string().min(1).describe(
    "One-sentence editorial reasoning shown above the list. Explains why these rows matter; do not repeat the per-row values."
  ),
  caption: e.string().min(1).meta({ id: "EntityListCaption", title: "EntityListCaption" }).nullish().describe(
    "States how the rows are ranked, e.g. 'Sortiert nach Rückgang in €, letzte 3 Monate gegen die 3 Monate davor'."
  ),
  rows: se
}).meta({
  id: "EntityList",
  title: "EntityList",
  description: "Generic ranked list of preformatted rows. Use a specialized card only when the renderer must compute from the numbers."
}), de = r("todoChecklist", {
  todoIds: e.array(e.string()).min(1).max(200).describe(
    "Ids of the open to-dos to show, in display order. The renderer loads title, due date, assignee and status live from the todos service and skips ids that are completed or deleted by render time."
  )
}).meta({
  id: "TodoChecklist",
  title: "TodoChecklist",
  description: "Open to-dos from the client's last recap as an interactive checklist. Emit it as the only talking point of the first story; the story disappears once every listed to-do is done."
}), le = e.object({
  label: e.string().min(1).describe("e.g. 'Letzte 3 Monate', 'Nicht fakturiert', 'Frühere Käufe'."),
  value: e.string().min(1).describe("e.g. '12 Aufträge', '5.532 €', '28'."),
  detail: e.string().min(1).meta({ id: "KpiDetail", title: "KpiDetail" }).nullish().describe(
    "Qualifies the value, e.g. 'Nov 2024 – Jul 2025' or 'bei 49 Aufträgen'."
  )
}).meta({
  id: "Kpi",
  title: "Kpi",
  description: "Every string is preformatted and localized by the producer."
}), l = e.array(le).min(1).meta({ id: "CardKpis", title: "CardKpis" }), k = e.object({
  caption: e.array(n).min(1).meta({ id: "ChartCaption", title: "ChartCaption" }).nullish().describe(
    "One sentence. Fragments join verbatim, so each carries its own spacing, e.g. 'Letzte Bestellung am 16.07.2025, ' followed by a negative 'seitdem kein Umsatz mehr'."
  ),
  granularity: e.enum(["month", "quarter"]).meta({ id: "ChartGranularity", title: "ChartGranularity" }).nullish().describe(
    "Period each data row covers. Absent means the spacing between the last two period starts sets it, so a chart with one row, or two rows where the first starts inside its period, needs it."
  ),
  chart: p.describe(
    "x.axisType is 'time', and each row's x value is the ISO start date of its period, consecutive and oldest first. The first row may start inside its period, e.g. '2026-08-25' on a month chart, and then covers only the days from that date."
  )
}).meta({
  id: "ChartSection",
  title: "ChartSection"
}), ce = e.string().min(1).meta({ id: "OrderScopeLabel", title: "OrderScopeLabel" }), he = e.number().min(0).meta({ id: "OpenOrderValue", title: "OpenOrderValue" }), P = () => ({
  dateRange: o.describe(
    "Bounds the scope, the set of orders every other field describes."
  ),
  scopeLabel: ce.nullish().describe(
    "Names the scope, e.g. 'Letzte 3 Monate', 'Seit August 2026' or 'Auftragsliste, gefiltert'. Absent falls back to the dates of dateRange."
  ),
  openOrderValue: he.nullish().describe(
    "Order value in scope not yet invoiced. A partially invoiced order counts only its open lines. Absent when unknown."
  )
}), g = e.object(P()).meta({
  id: "OrdersSection",
  title: "OrdersSection"
}), me = e.enum(["open", "partiallyInvoiced", "invoiced"]).meta({
  id: "OrderInvoiceStatus",
  title: "OrderInvoiceStatus"
}), ue = e.object({
  id: e.uuid(),
  date: e.iso.date(),
  externalReference: e.string().min(1).describe("Order number from the ERP, e.g. 'AB-12345'."),
  incomingOrder: e.number().nullish(),
  invoiceStatus: me.nullish().describe(
    "Absent when the invoice status is unknown."
  )
}).meta({
  id: "OrderRow",
  title: "OrderRow"
}), pe = e.array(ue).min(1).max(3).meta({ id: "OrderRows", title: "OrderRows" }), C = () => e.number().int().min(1), S = () => ({
  kind: e.literal("rhythm"),
  normalCycleDays: C()
}), ge = e.object({ ...S(), currentCycleDays: C() }).meta({
  id: "OrderRhythmCurrentCycle",
  title: "OrderRhythmCurrentCycle"
}), be = e.object({ ...S(), daysSinceLastOrder: e.number().int().min(0) }).meta({
  id: "OrderRhythmSinceLastOrder",
  title: "OrderRhythmSinceLastOrder"
}), ye = e.union([ge, be]).meta({
  id: "OrderRhythmSection",
  title: "OrderRhythmSection"
}), fe = e.object({
  kind: e.literal("kpis"),
  items: l.describe(
    "e.g. the order count of the last 3 months and of the 3 months before, or the order intake and the amount not yet invoiced."
  )
}).meta({
  id: "OrderKpisSection",
  title: "OrderKpisSection"
}), we = e.object({
  kind: e.literal("orders"),
  ...P(),
  items: pe.nullish().describe(
    "Every order in scope, newest first. Absent when the scope holds more than 3."
  )
}).meta({
  id: "OrderListSection",
  title: "OrderListSection",
  description: "The scope holds at least one order."
}), ve = e.union([ye, fe, we]).meta({
  id: "OrderCardSection",
  title: "OrderCardSection"
}), Oe = r("orderCard", {
  label: n.nullish().describe(
    "Caption for the headline, e.g. 'Aufträge · letzte 3 Monate'."
  ),
  headline: e.string().min(1).describe("e.g. 'Der gewohnte Takt ist abgerissen'."),
  summary: e.string().min(1).nullish().describe("One sentence at most."),
  sections: e.array(ve).min(1).describe("In display order.")
}).meta({
  id: "OrderCard",
  title: "OrderCard"
}), ke = r("basketCard", {
  label: n.nullish().describe(
    "Caption for the headline, e.g. 'Kunde · seit August 2026'."
  ),
  headline: e.string().min(1).describe("e.g. 'Die Aufträge werden größer'."),
  kpis: l.describe(
    "e.g. the revenue since the first order and the average order value."
  ),
  chart: k.describe("Revenue per period since the first order."),
  orders: g.describe("The scope starts at the first order.")
}).meta({
  id: "BasketCard",
  title: "BasketCard"
}), Pe = r("reentryCard", {
  label: n.nullish().describe(
    "Caption for the product, e.g. 'Früher in jedem vierten Auftrag'."
  ),
  product: h.describe(
    "A product the customer bought regularly before and no longer buys."
  ),
  kpis: l.describe(
    "e.g. the number of earlier purchases and the potential per year."
  ),
  orders: g.describe(
    "Narrowed to the orders that contain product."
  )
}).meta({
  id: "ReentryCard",
  title: "ReentryCard"
}), Ce = r("lifetimeCard", {
  label: n.nullish().describe(
    "Caption for the headline, e.g. 'Umsatz · letzte 24 Monate'."
  ),
  headline: e.string().min(1).describe("e.g. 'Bis Juli 2025 fast jede Woche ein Auftrag'."),
  kpis: l.describe(
    "e.g. the revenue of the active phase and the average per order."
  ),
  chart: k.describe(
    "Revenue per period. Periods after the last order go to a second y series with negative sentiment, labeled e.g. 'Kein Umsatz' and holding 0, so each row fills exactly one of the two series."
  ),
  orders: g
}).meta({
  id: "LifetimeCard",
  title: "LifetimeCard"
}), Se = e.discriminatedUnion("type", [
  Y,
  F,
  B,
  q,
  V,
  H,
  J,
  $,
  oe,
  de,
  Oe,
  ke,
  Pe,
  Ce
]).meta({
  title: "TalkingPoint",
  description: "One card in a story. A story is a sequence of talking points; their number and order are the agent's call."
}), T = e.object({
  type: e.literal("storyCard"),
  storyKey: e.string().min(1).nullish().describe("Stable semantic key identifying the story across regenerations."),
  category: e.string().min(1).describe("Free-text category for the story (e.g. 'Wachstum')."),
  sentiment: d.nullish().describe(
    "Story-level directional tone. Absent means no particular direction."
  ),
  storyHeadline: e.string().min(1),
  description: e.string().min(1).describe("Short prose summary of the story."),
  potential: e.string().min(1).nullish().describe(
    "Optional preformatted, localized upside value for the story (e.g. '+71.000 €')."
  ),
  talkingPoints: e.array(Se).min(1).describe("Cards that make up the story.")
}).meta({
  id: "StoryCard",
  title: "StoryCard",
  description: "One customer story: a category, headline, summary, optional potential and sentiment, plus one or more talking points."
}), Te = e.union([e.string(), e.number(), e.null()]), Re = e.object({
  type: e.literal("table"),
  title: e.string().min(1),
  description: e.string().min(1).nullish(),
  data: e.array(e.record(e.string(), Te)).describe(
    "Table rows as key-value bags. Keys are column ids, values are string, number, or null for empty cells."
  )
}).meta({
  id: "TablePart",
  title: "TablePart",
  description: "Tabular data part: title, optional description, and a row-bag array."
}), xe = e.object({
  type: e.literal("chart"),
  title: e.string().min(1),
  description: e.string().min(1).nullish(),
  chart: p
}).meta({
  id: "ChartPart",
  title: "ChartPart",
  description: "Standalone chart part: title, optional description, and a chart."
}), De = [u, T, Re, xe], Ae = [u], je = [T, u], Le = e.discriminatedUnion("type", De).meta({
  id: "AgentMessagePart",
  title: "AgentMessagePart",
  description: "Full agent UI part vocabulary (ADR-0001)."
}), Ee = e.discriminatedUnion("type", Ae).meta({
  id: "ChatMessagePart",
  title: "ChatMessagePart",
  description: "Parts valid in agent chat."
}), Me = e.discriminatedUnion("type", je).meta({
  id: "MeetingPrepMessagePart",
  title: "MeetingPrepMessagePart",
  description: "Parts valid in a meeting-prep response: a story card, or a text fallback when no story qualifies."
}), Ue = e.object({
  sessionId: e.uuid(),
  messageParts: e.array(Me).min(1)
}).meta({
  id: "MeetingPrepResponse",
  title: "MeetingPrepResponse",
  description: "Top-level meeting-prep response. The agents service streams one MeetingPrepMessagePart per messagePart event."
}), ze = () => {
  const { schemas: t } = e.toJSONSchema(e.globalRegistry, {
    target: "draft-2020-12",
    reused: "ref",
    uri: (s) => `#/$defs/${s}`
  }), { MeetingPrepResponse: i, ...a } = t;
  return m({ ...i, $defs: a });
}, Ke = /* @__PURE__ */ new Set(["uuid"]), m = (t) => {
  if (Array.isArray(t)) return t.map(m);
  if (t === null || typeof t != "object") return t;
  const i = Object.entries(t), a = i.some(([s, c]) => s === "format" && Ke.has(String(c)));
  return Object.fromEntries(
    i.filter(([s]) => !(a && s === "pattern")).map(([s, c]) => [s, m(c)])
  );
};
export {
  Le as AgentMessagePart,
  p as BarChart,
  ke as BasketCard,
  l as CardKpis,
  E as ChartMeta,
  xe as ChartPart,
  k as ChartSection,
  Ee as ChatMessagePart,
  x as DateRange,
  o as DateRangeWindow,
  oe as EntityList,
  se as EntityRows,
  q as GapList,
  G as GapListItem,
  le as Kpi,
  J as KpiDelta,
  D as KpiPeriod,
  Ce as LifetimeCard,
  Me as MeetingPrepMessagePart,
  Ue as MeetingPrepResponse,
  y as NormalCycle,
  H as Offer,
  Oe as OrderCard,
  ve as OrderCardSection,
  me as OrderInvoiceStatus,
  f as OrderKpis,
  fe as OrderKpisSection,
  we as OrderListSection,
  B as OrderPulseCalendar,
  F as OrderPulseHero,
  ge as OrderRhythmCurrentCycle,
  ye as OrderRhythmSection,
  be as OrderRhythmSinceLastOrder,
  ue as OrderRow,
  g as OrdersSection,
  $ as Overview,
  X as OverviewItem,
  _ as PeerPotentialsDrill,
  h as Product,
  te as ProductEntity,
  Z as ProductKpisDrill,
  ee as ProductRowDrill,
  ie as ProductRows,
  R as ProductTrend,
  b as PulseStatus,
  N as QuestionPromptItem,
  V as QuestionPrompts,
  Pe as ReentryCard,
  v as RowText,
  d as Sentiment,
  n as SentimentText,
  T as StoryCard,
  Re as TablePart,
  Se as TalkingPoint,
  Q as TermsOffer,
  u as TextPart,
  ne as TextRows,
  de as TodoChecklist,
  Y as Trend,
  M as ValueFormat,
  W as ValueOffer,
  I as XAxis,
  L as YSeries,
  K as YSeriesStyle,
  A as cardKeyField,
  ze as emitJsonSchema,
  P as ordersSectionFields,
  r as talkingPointCard
};
//# sourceMappingURL=index.js.map
